import {
  createAnimation,
  IonCard,
  IonCardContent,
  IonItem,
  IonItemOption,
  IonItemOptions,
  IonItemSliding,
  IonText,
} from '@ionic/react';
import { useState, type MouseEvent } from 'react';
import { useHistory } from 'react-router';

import { Icon } from '@/components/Icon';
import { IconRotate } from '@/components/Icon/types';

import { convertDate } from '@/utils/convertFirebaseDate';
import { mergeClassNames } from '@/utils/mergeClassNames';

import userService from '@/hooks/useClients/userService';

import { ExportedClient } from '@/models';

import styles from './ClientCard.module.css';

import type { Animation } from '@ionic/react';

import { DeleteClientModal } from '../DeleteClientModal/DeleteClientModal';

// Images

type ClientCardProps = {
  client: ExportedClient;
  location?: string;
  className?: string;
  onEditClick?: (clientId: string) => void;
};

export const ClientCard = ({ client, location, className, onEditClick }: ClientCardProps) => {
  const dateOfBirth = convertDate(client.dateOfBirth);
  const lastSession = client.lastSession ? convertDate(client.lastSession) : '-';
  const history = useHistory();
  const path = location ? `/${location}/${client.id}` : `/clients/${client.id}`;
  const [isDeleteClientModalOpen, setIsDeleteClientModalOpen] = useState(false);

  const handleClick = (event: MouseEvent<HTMLIonCardContentElement>) => {
    event.preventDefault();
    event.stopPropagation();

    history.push(path, {
      clientName: `${client.firstName} ${client.lastName}`,
      clientId: client.id,
      resetActiveSession: true,
      measurementType: location,
    });
  };

  const handleArchive = (clientId: string, event?: MouseEvent<HTMLIonItemOptionElement>) => {
    event!.stopPropagation();
    event!.preventDefault();

    console.log('Archive client:', client.id);
    userService.archiveClient(client.id);

    // Reset content position after archiving
    const cardElement = document.getElementById(`client-content-${clientId}`);
    if (cardElement) {
      const animation: Animation = createAnimation()
        .addElement(cardElement)
        .duration(400)
        .to('transform', 'translateX(-120%)')
        .to('height', '0px')
        .to('display', 'none');
      animation.play();
    }
  };

  const handleEditUser = (event: MouseEvent<HTMLIonItemOptionElement>) => {
    event.stopPropagation();
    event.preventDefault();

    onEditClick?.(client.id);
  };

  return (
    <>
      <IonItemSliding className={mergeClassNames(styles.sliding, className)} id={`client-content-${client.id}`}>
        <IonItem className={styles.item}>
          <IonCard className={styles.card} onClick={handleClick}>
            <IonCardContent className={styles.cardContent}>
              <div className={styles.userCol}>
                <Icon className={styles.icon} color='#2DD2BE' name='avatar' />
                <IonText className={styles.name}>{`${client.firstName} ${client.lastName}`}</IonText>
              </div>

              <IonText className={styles.birthdate}>{dateOfBirth}</IonText>

              <IonText className={styles.lastRecording}>{lastSession}</IonText>

              <Icon
                className={mergeClassNames(styles.icon, styles.chevron)}
                color='var(--ion-color-grey500)'
                direction={IconRotate.East}
                name='chevron'
              />
            </IonCardContent>
          </IonCard>
        </IonItem>

        <IonItemOptions className={styles.options}>
          <IonItemOption
            className={mergeClassNames(styles.option, styles.archive)}
            onClick={() => setIsDeleteClientModalOpen(true)}>
            <Icon color='#E7364B' name='delete' size={24} />
          </IonItemOption>
          <IonItemOption className={mergeClassNames(styles.option, styles.edit)} onClick={handleEditUser}>
            <Icon color='#667A7A' name='edit' size={24} />
          </IonItemOption>
        </IonItemOptions>
      </IonItemSliding>

      <DeleteClientModal
        clientName={client.firstName + ' ' + client.lastName}
        handleClose={() => setIsDeleteClientModalOpen(false)}
        isOpen={isDeleteClientModalOpen}
        onConfirm={() => handleArchive(client.id)}
      />
    </>
  );
};
